import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert } from 'react-native';
import { Card, Button, ActivityIndicator, Divider } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ESTADOS_CONFIG = {
  pendiente: { label: 'Pendiente', color: '#FFC107', icon: 'time' },
  aprobada: { label: 'Aprobada', color: '#17A2B8', icon: 'checkmark-done' },
  pagada: { label: 'Pagada', color: '#28A745', icon: 'checkmark-circle' },
  cancelada: { label: 'Cancelada', color: '#DC3545', icon: 'close-circle' },
};

const ComisionDetalleScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user } = useAuth();
  const { comisionId } = route.params;
  const [comision, setComision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = user && ['admin', 'supervisor'].includes(user.rol);

  useEffect(() => {
    loadComision();
  }, [comisionId]);

  const loadComision = async () => {
    try {
      setLoading(true);
      const data = await api.getComision(comisionId);
      setComision(data);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar la comisión');
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = () => {
    Alert.alert('Aprobar Comisión', `¿Aprobar comisión de L. ${parseFloat(comision.monto_total).toFixed(2)}?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Aprobar', onPress: async () => {
          try {
            setActionLoading(true);
            await api.aprobarComision(comisionId);
            Alert.alert('Aprobada', 'Comisión aprobada exitosamente');
            loadComision();
          } catch (error) {
            Alert.alert('Error', error.message);
          } finally {
            setActionLoading(false);
          }
        }
      }
    ]);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  if (!comision) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Comisión no encontrada</Text>
      </View>
    );
  }

  const estadoConfig = ESTADOS_CONFIG[comision.estado] || ESTADOS_CONFIG.pendiente;

  return (
    <ScrollView style={styles.container}>
      {/* Header */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerRow}>
            <Text style={styles.title}>Comisión #{comision.id}</Text>
            <View style={[styles.estadoBadge, { backgroundColor: estadoConfig.color }]}>
              <Ionicons name={estadoConfig.icon} size={14} color="#fff" />
              <Text style={styles.estadoText}>{estadoConfig.label}</Text>
            </View>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.infoRow}>
            <Text style={styles.label}>Vendedor:</Text>
            <Text style={styles.value}>{comision.vendedor_nombre}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Venta:</Text>
            <Text style={styles.value}>#{comision.venta_id}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Cliente:</Text>
            <Text style={styles.value}>{comision.cliente_nombre || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Total Venta:</Text>
            <Text style={styles.value}>L. {parseFloat(comision.venta_total || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Comisión:</Text>
            <Text style={[styles.value, styles.montoTotal]}>L. {parseFloat(comision.monto_total).toFixed(2)}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Fecha:</Text>
            <Text style={styles.value}>{new Date(comision.created_at).toLocaleString('es-HN')}</Text>
          </View>
          {comision.aprobado_por_nombre && (
            <View style={styles.infoRow}>
              <Text style={styles.label}>Aprobado por:</Text>
              <Text style={styles.value}>{comision.aprobado_por_nombre}</Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Detalles por producto */}
      <Card style={styles.card}>
        <Card.Title title="Desglose por Producto" />
        <Card.Content>
          {comision.detalles && comision.detalles.map((det, index) => (
            <View key={det.id || index}>
              {index > 0 && <Divider style={styles.divider} />}
              <View style={styles.detalleItem}>
                <View style={styles.detalleInfo}>
                  <Text style={styles.detalleProducto}>{det.producto_nombre || `Producto #${det.producto_id}`}</Text>
                  <Text style={styles.detalleSubtitle}>
                    Subtotal: L. {parseFloat(det.subtotal_venta).toFixed(2)} x {det.porcentaje_aplicado}%
                    {parseFloat(det.monto_fijo_aplicado) > 0 ? ` + L.${parseFloat(det.monto_fijo_aplicado).toFixed(2)} x ${det.cantidad}` : ''}
                  </Text>
                </View>
                <Text style={styles.detalleMonto}>L. {parseFloat(det.monto_comision).toFixed(2)}</Text>
              </View>
            </View>
          ))}
        </Card.Content>
      </Card>

      {/* Acciones */}
      {isAdmin && comision.estado === 'pendiente' && (
        <Card style={styles.card}>
          <Card.Content>
            <Button
              mode="contained"
              onPress={handleAprobar}
              loading={actionLoading}
              disabled={actionLoading}
              style={[styles.actionButton, { backgroundColor: '#17A2B8' }]}
              icon="check"
            >
              Aprobar Comisión
            </Button>
          </Card.Content>
        </Card>
      )}

      {isAdmin && comision.estado === 'aprobada' && (
        <Card style={styles.card}>
          <Card.Content>
            <Button
              mode="contained"
              onPress={() => navigation.navigate('PagoComisiones', { vendedorId: comision.vendedor_id })}
              style={[styles.actionButton, { backgroundColor: '#28A745' }]}
              icon="cash"
            >
              Ir a Pagar
            </Button>
          </Card.Content>
        </Card>
      )}

      <View style={styles.bottomSpacer} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card: { margin: 8, elevation: 2 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  title: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  estadoBadge: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12,
  },
  estadoText: { color: '#fff', fontSize: 12, fontWeight: '600', marginLeft: 4 },
  divider: { marginVertical: 8 },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 4 },
  label: { fontSize: 14, color: '#666' },
  value: { fontSize: 14, fontWeight: '600', color: '#333' },
  montoTotal: { fontSize: 18, color: '#28A745', fontWeight: 'bold' },
  detalleItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  detalleInfo: { flex: 1, marginRight: 12 },
  detalleProducto: { fontSize: 14, fontWeight: '600', color: '#333' },
  detalleSubtitle: { fontSize: 12, color: '#666', marginTop: 2 },
  detalleMonto: { fontSize: 15, fontWeight: 'bold', color: '#28A745' },
  actionButton: { marginVertical: 4 },
  bottomSpacer: { height: 20 },
});

export default ComisionDetalleScreen;
